import React from 'react';
import { Card, Tag, Button, Timeline, Space, Typography, Tooltip } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  FireOutlined,
  UserOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';
import './TicketCard.css';

const { Text, Paragraph } = Typography;

const TicketCard = ({
  ticket,
  onUpdateStatus,
  onAddUpdate,
  isEditable = true
}) => {
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical':
        return <FireOutlined style={{ color: '#ff4d4f' }} />;
      case 'high':
        return <WarningOutlined style={{ color: '#ff7a45' }} />;
      case 'medium':
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      case 'low':
        return <ClockCircleOutlined style={{ color: '#52c41a' }} />;
      default:
        return null;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'gold';
      case 'low':
        return 'green';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'blue';
      case 'in_progress':
        return 'orange';
      case 'resolved':
        return 'green';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatStatus = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (e) {
      return dateString;
    }
  };

  const handleStatusChange = (newStatus) => {
    if (onUpdateStatus) {
      onUpdateStatus(ticket.id, newStatus);
    }
  };

  const handleAddComment = () => {
    if (onAddUpdate) {
      onAddUpdate(ticket.id);
    }
  };

  return (
    <Card
      className="ticket-card"
      title={
        <Space>
          {getPriorityIcon(ticket.priority)}
          <Text strong>{ticket.ticket_number}</Text>
          <Text>{ticket.title}</Text>
        </Space>
      }
      extra={
        <Space>
          <Tag color={getPriorityColor(ticket.priority)}>
            {ticket.priority.toUpperCase()}
          </Tag>
          <Tag color={getStatusColor(ticket.status)}>
            {formatStatus(ticket.status)}
          </Tag>
        </Space>
      }
    >
      <div className="ticket-card-content">
        {/* Description */}
        <div className="ticket-section">
          <Text type="secondary">Description</Text>
          <Paragraph>{ticket.description}</Paragraph>
        </div>

        {/* Metadata */}
        <div className="ticket-metadata">
          <Space direction="vertical" size="small">
            {ticket.assigned_to && (
              <Text>
                <UserOutlined /> Assigned to: <strong>{ticket.assigned_to}</strong>
              </Text>
            )}
            <Text type="secondary">
              Created: {formatDate(ticket.created_at)}
            </Text>
            {ticket.resolved_at && (
              <Text type="secondary">
                Resolved: {formatDate(ticket.resolved_at)}
              </Text>
            )}
          </Space>
        </div>

        {/* Updates Timeline */}
        {ticket.updates && ticket.updates.length > 0 && (
          <div className="ticket-section ticket-updates">
            <Text type="secondary">Updates</Text>
            <Timeline
              mode="left"
              items={ticket.updates.map((update) => ({
                color: update.new_status === 'resolved' ? 'green' : 'blue',
                children: (
                  <div className="ticket-update-item">
                    <Paragraph>{update.update_text}</Paragraph>
                    {update.old_status && update.new_status && (
                      <Text type="secondary" className="status-change">
                        Status: {formatStatus(update.old_status)} → {formatStatus(update.new_status)}
                      </Text>
                    )}
                    <Text type="secondary" className="update-timestamp">
                      {formatDate(update.created_at)}
                    </Text>
                  </div>
                )
              }))}
            />
          </div>
        )}

        {/* Actions */}
        {isEditable && (
          <div className="ticket-actions">
            <Space>
              {ticket.status === 'open' && (
                <Button
                  type="primary"
                  onClick={() => handleStatusChange('in_progress')}
                >
                  Start Working
                </Button>
              )}
              {ticket.status === 'in_progress' && (
                <Button
                  type="primary"
                  onClick={() => handleStatusChange('resolved')}
                >
                  Mark Resolved
                </Button>
              )}
              {ticket.status === 'resolved' && (
                <Button
                  onClick={() => handleStatusChange('closed')}
                >
                  Close Ticket
                </Button>
              )}
              <Button onClick={handleAddComment}>
                Add Comment
              </Button>
            </Space>
          </div>
        )}
      </div>
    </Card>
  );
};

export default TicketCard;
